import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import LyricConfirm from './LyricConfirm';
import './GoogleDocsApiModal.css';

/**
 * Owner/admin panel to manage a deployed app's Google Docs relay access.
 *
 * Lets the app owner enable the relay (mints a per-app key), view/copy/rotate
 * that key, disable it, and copy a ready-to-run usage snippet. The app then
 * POSTs to /api/docs-api/fetch with the key to reach Lyric's shared Google
 * Docs service account (one admin-configured credential, brokered so the app
 * never needs its own Google OAuth). See server/routes/googleDocsApi.js.
 *
 * Props:
 *   appName     registry key of the app
 *   displayName friendly name (for the header + snippet)
 *   apiUrl      API base URL
 *   authFetch   (url, opts) => fetch, injects the Okta bearer token
 *   onClose     close the modal
 */
const GoogleDocsApiModal = ({ appName, displayName, apiUrl, authFetch, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null); // { docsEnabled, apiKey, docsConfigured, rateLimit }
  const [busy, setBusy] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [redeployNeeded, setRedeployNeeded] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [applied, setApplied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/google-docs`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, appName, authFetch]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmRotate) onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, confirmRotate]);

  const act = useCallback(async (path, label) => {
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/google-docs/${path}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await load();
      if (label === 'rotate-key' || label === 'enable') setRevealKey(true);
      // enable/rotate return redeployRequired when the app is live — its env is
      // only re-read on respawn, so surface the "Redeploy now" prompt.
      if (label === 'disable') { setRedeployNeeded(false); setApplied(false); }
      else { setRedeployNeeded(!!data.redeployRequired); setApplied(false); }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [apiUrl, appName, authFetch, load]);

  const applyRedeploy = useCallback(async () => {
    setRedeploying(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/google-docs/apply`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRedeployNeeded(false);
      setApplied(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeploying(false);
    }
  }, [apiUrl, appName, authFetch]);

  const copy = useCallback(async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard blocked — user can select manually */ }
  }, []);

  const key = status?.apiKey || '';
  const enabled = !!status?.docsEnabled;
  const maskedKey = key ? `${key.slice(0, 9)}${'•'.repeat(Math.max(0, key.length - 13))}${key.slice(-4)}` : '';
  const fetchUrl = `${apiUrl}/api/docs-api/fetch`;

  // Reference the injected env var rather than the literal key, so "Copy"
  // produces a runnable command without embedding the secret.
  const curlSnippet = key
    ? `curl -X POST ${fetchUrl} \\
  -H "Authorization: Bearer $LYRIC_DOCS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://docs.google.com/document/d/<doc-id>/edit"
  }'`
    : '';

  return ReactDOM.createPortal(
    <div className="docs-api-overlay" onClick={(e) => { if (e.target === e.currentTarget && !confirmRotate) onClose(); }}>
      <div className="docs-api-card" role="dialog" aria-modal="true" aria-label="Google Docs API settings">
        <div className="docs-api-header">
          <span className="docs-api-brand" aria-hidden="true">📄</span>
          <div>
            <h3>Google Docs API</h3>
            <div className="docs-api-sub">{displayName || appName}</div>
          </div>
          <button className="docs-api-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="docs-api-body">
          {loading ? (
            <div className="docs-api-muted">Loading…</div>
          ) : error ? (
            <div className="docs-api-error">{error}</div>
          ) : (
            <>
              {status && !status.docsConfigured && (
                <div className="docs-api-warn">
                  ⚠️ No Google service account is configured on the Lyric server yet — enabling here
                  works, but fetches will fail with 503 until an admin sets{' '}
                  <code>GOOGLE_DOCS_CLIENT_EMAIL</code> / <code>GOOGLE_DOCS_PRIVATE_KEY</code>.
                </div>
              )}

              <div className="docs-api-row">
                <span className="docs-api-label">Status</span>
                <span className={`docs-api-badge ${enabled ? 'on' : 'off'}`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {enabled && key && (
                <div className="docs-api-keybox">
                  <span className="docs-api-label">API key</span>
                  <code className="docs-api-key">{revealKey ? key : maskedKey}</code>
                  <div className="docs-api-keybtns">
                    <button onClick={() => setRevealKey((v) => !v)}>{revealKey ? 'Hide' : 'Reveal'}</button>
                    <button onClick={() => copy(key, 'key')}>{copied === 'key' ? 'Copied!' : 'Copy'}</button>
                  </div>
                </div>
              )}

              {enabled && key && (
                <div className="docs-api-inject">
                  ✓ Lyric injects this key into your app automatically as the{' '}
                  <code>LYRIC_DOCS_API_KEY</code> environment variable (plus{' '}
                  <code>LYRIC_DOCS_API_BASE_URL</code>) — no secret to set by hand. It takes effect on
                  the app’s next (re)deploy.
                </div>
              )}

              {redeployNeeded && (
                <div className="docs-api-redeploy">
                  <div className="docs-api-redeploy-text">
                    Redeploy <b>{displayName || appName}</b> to apply the key to the running app.
                  </div>
                  <button className="docs-api-btn primary" disabled={redeploying} onClick={applyRedeploy}>
                    {redeploying ? 'Redeploying…' : 'Redeploy now'}
                  </button>
                </div>
              )}

              {applied && !redeployNeeded && (
                <div className="docs-api-inject">
                  🚀 Redeploy started — the key is injected on respawn. Give it a moment, then test a fetch.
                </div>
              )}

              {status?.rateLimit && (
                <div className="docs-api-meta">
                  <span>Limit: <b>{status.rateLimit.max}</b> / {Math.round((status.rateLimit.windowMs || 0) / 60000)} min</span>
                </div>
              )}

              {enabled && key && (
                <div className="docs-api-snippet">
                  <div className="docs-api-snippet-head">
                    <span className="docs-api-label">Usage</span>
                    <button onClick={() => copy(curlSnippet, 'curl')}>{copied === 'curl' ? 'Copied!' : 'Copy'}</button>
                  </div>
                  <pre>{curlSnippet}</pre>
                  <div className="docs-api-muted small">
                    POST to <code>/api/docs-api/fetch</code> with a JSON body: <code>url</code> (a Google
                    Doc link or bare document ID). The response is{' '}
                    <code>{'{ ok, documentId, title, text }'}</code> (the doc's plain text). In code, read
                    the key from <code>process.env.LYRIC_DOCS_API_KEY</code> and the base URL from{' '}
                    <code>process.env.LYRIC_DOCS_API_BASE_URL</code>.
                  </div>
                </div>
              )}

              <div className="docs-api-actions">
                {!enabled ? (
                  <button className="docs-api-btn primary" disabled={busy} onClick={() => act('enable', 'enable')}>
                    {busy ? 'Enabling…' : 'Enable Google Docs API'}
                  </button>
                ) : (
                  <>
                    <button className="docs-api-btn" disabled={busy} onClick={() => setConfirmRotate(true)}>
                      Rotate key
                    </button>
                    <button className="docs-api-btn danger" disabled={busy} onClick={() => act('disable', 'disable')}>
                      {busy ? 'Disabling…' : 'Disable'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <LyricConfirm
        isOpen={confirmRotate}
        tone="danger"
        title="Rotate API key?"
        message="The current key stops working immediately. Any app or script using it must be updated with the new key."
        confirmLabel="Rotate key"
        onConfirm={() => { setConfirmRotate(false); act('rotate-key', 'rotate-key'); }}
        onCancel={() => setConfirmRotate(false)}
      />
    </div>,
    document.body,
  );
};

export default GoogleDocsApiModal;
